import { useQueryClient, useMutation } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { workspaceIdAtom } from "../../../atoms/WorkspaceAtoms";
import {
  callFunctionIfExists,
  getErrorMessage,
  groupTagData,
} from "../../../utils/GeneralUtils";
import TenantAPI from "../../api/TenantAPI";

/**
 * useCreateTag - Hook to create a tag. Uses react-query to cache results. Flexible for all scopes
 */
const useCreateTag = (fromSelectableInput = false) => {
  const queryClient = useQueryClient();
  const workspaceID = useAtomValue(workspaceIdAtom);

  const addNewTag = (scope: any, newTag: any) => {
    if (fromSelectableInput) {
      return queryClient.setQueryData(
        ["all-tags", workspaceID],
        (prev?: any[]) => [...(prev || []), newTag]
      );
    }

    queryClient.setQueryData(["tags", scope], (prev: any) => ({
      tags: [...prev.tags, newTag],
      tagGroups: groupTagData([...prev.tags, newTag]),
    }));
  };

  const createTag = useMutation({
    mutationFn: ({
      scope,
      payload,
    }: {
      scope: any;
      payload: any;
      onSuccess?: () => void;
    }) => {
      payload.Scope = scope;

      if (scope.Type === "Cloud") {
        return TenantAPI.createCloudTag(payload).then((data) =>
          addNewTag(scope, data.data.data)
        );
      }

      if (scope.Type === "Workspace") {
        return TenantAPI.createWorkspaceTag(scope.WorkspaceID, payload).then(
          (data) => addNewTag(scope, data.data.data)
        );
      }

      if (scope.Type === "Module") {
        return TenantAPI.createModuleTag(scope.ModuleAPIName, payload).then(
          (data) => addNewTag(scope, data.data.data)
        );
      }

      if (scope.Type === "Person") {
        return TenantAPI.createUserTag(scope.PersonID, payload).then((data) =>
          addNewTag(scope, data.data.data)
        );
      }

      return Promise.reject("Invalid Scope Provided to useCreateTag");
    },
    onSuccess: (_, { onSuccess }) => {
      callFunctionIfExists(onSuccess);
    },
  });

  return {
    createTag: createTag.mutate,
    creatingTag: createTag.isPending,
    createError: createTag.error ? getErrorMessage(createTag.error) : null,
  };
};

export default useCreateTag;
